<template>
    <div>
        <app-bar :title="t('premium_one_month_trial_title')"></app-bar>

        <v-layout>
            <v-progress-linear
                v-if="overlay"
                absolute
                indeterminate
            ></v-progress-linear>

            <card-collection
                :meta="meta"
                :busy="overlay"
                @updated="updated"
            >
                <template #filters>
                    <v-toolbar
                        elevation="0"
                        color="transparent"
                        class="mt-7"
                        height="56"
                    >
                        <v-text-field
                            v-model="queries.search"
                            @click:clear="clearSearch"
                            class="input-search md mr-2"
                            clearable
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-magnify"
                            :label="t('general_label_search')"
                        ></v-text-field>

                        <span class="sub-label px-5">{{ t('general_label_date_period') }}</span>

                        <date-picker-modal
                            :value="queries.date_from"
                            @updated="(v) => queries.date_from = v"
                        >
                            <template #default="{ on, attrs }">
                                <div v-on="on" v-bind="attrs">
                                    <v-text-field
                                        v-model="queries.date_from"
                                        class="xs input-color-inherit"
                                        outlined
                                        dense
                                        hide-details
                                        readonly
                                        :label="t('general_label_from')"
                                    ></v-text-field>
                                </div>
                            </template>
                        </date-picker-modal>

                        <span class="sub-label px-2">-</span>

                        <date-picker-modal
                            :value="queries.date_to"
                            @updated="(v) => queries.date_to = v"
                        >
                            <template #default="{ on, attrs }">
                                <div v-on="on" v-bind="attrs">
                                    <v-text-field
                                        v-model="queries.date_to"
                                        class="xs input-color-inherit"
                                        outlined
                                        dense
                                        hide-details
                                        readonly
                                        :label="t('general_label_to')"
                                    ></v-text-field>
                                </div>
                            </template>
                        </date-picker-modal>

                        <v-btn
                            @click="search"
                            class="sm mx-5"
                            color="primary"
                        >{{ t('general_action_search') }}</v-btn>

                        <v-spacer></v-spacer>

                        <v-btn
                            v-ripple="false"
                            plain
                            color="orange darken-4"
                            @click="reset"
                        >{{ t('general_action_reset_filters') }}</v-btn>
                    </v-toolbar>
                </template>

                <template #data>
                    <thead>
                    <tr>
                        <th>{{ t('premium_label_user_id') }}</th>
                        <th>{{ t('user_label_email_address') }}</th>
                        <th>{{ t('premium_label_product') }}</th>
                        <th>{{ t('premium_label_start_date') }}</th>
                        <th>{{ t('premium_label_end_date') }}</th>
                        <th>{{ t('premium_label_has_personalized_program') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="collection.data.length === 0">
                        <td class="text-center" colspan="6">{{ t('general_text_no_data') }}</td>
                    </tr>
                    <tr v-for="item in collection.data" :key="item.id">
                        <td>{{ item.userId }}</td>
                        <td>{{ item.email }}</td>
                        <td>{{ item.product }}</td>
                        <td>{{ item.startDate ? item.startDate.format('DD/MM/YYYY HH:mm') : '-' }}</td>
                        <td>{{ item.endDate ? item.endDate.format('DD/MM/YYYY HH:mm') : '-' }}</td>
                        <td>{{ item.hasPersonalizedProgram ? t('general_text_yes') : t('general_text_no') }}</td>
                    </tr>
                    </tbody>
                </template>
            </card-collection>
        </v-layout>
    </div>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, toRefs } from '@vue/composition-api'
import { useI18n } from 'vue-i18n-composable'
import { useStore } from '@/store'
import CardCollection from '@/components/CardCollection.vue'
import DatePickerModal from '@/components/DatePickerModal.vue'

export default defineComponent({
    components: {
        CardCollection,
        DatePickerModal,
    },

    setup() {
        const state = reactive({
            overlay: true,
            meta: undefined,
        })

        const queries = reactive({
            search: <string | undefined>undefined,
            date_from: <string | undefined>undefined,
            date_to: <string | undefined>undefined,
            page: 1,
            per_page: 20,
        })

        const store = useStore()
        const collection = computed(() => store.getters['premiumTrialCollection/get'])

        const capture = () => {
            state.overlay = true
            store.dispatch('premiumTrialCollection/get', { ...queries }).then((data: any) => {
                state.meta = data.meta
                state.overlay = false
            })
        }

        const updated = (updatedQueries: any) => {
            queries.page = updatedQueries.page
            capture()
        }

        const search = () => capture()

        const clearSearch = () => {
            queries.search = undefined
            capture()
        }

        const reset = () => {
            queries.search = undefined
            queries.date_from = undefined
            queries.date_to = undefined
            queries.page = 1
            capture()
        }

        onMounted(() => capture())

        return {
            queries,
            collection,
            updated,
            search,
            clearSearch,
            reset,
            ...toRefs(state),
            ...useI18n(),
        }
    },
})
</script>
